/**
 * AddTxScreen — pixel-faithful re-port of `design/project/screens-add.jsx`.
 *
 * Layout (top → bottom, all on one scroll surface):
 *   1. Header — close X / segmented kind toggle / 📷 receipt
 *   2. Hero amount block — category icon + name, currency chip, expression
 *   3. Meta chip row — date / book / project / note
 *   4. Main category tab row (text-only pills, horizontal scroll)
 *   5. Sub-category grid (icon + label, 5-col)
 *   6. Keypad — 4×4 digits/ops + action row 清除 / 繼續新增 / 完成
 *   7. Edit-mode footer — destructive 刪除此筆 (with confirm sheet)
 *
 * The keypad is built INLINE (not via the shared `<Calculator>` component)
 * because the design's action row is `清除 / 繼續新增 / 完成` while
 * `<Calculator>` ships a `清除 / =` row and is consumed by other surfaces.
 * State transitions still go through `calcReduce`, so the rounding
 * semantics in `expression.ts` remain the single source of truth.
 *
 * Sharp edges enforced here:
 *   - Submit is **disabled** when `value.amountMinor <= 0`.
 *   - Submit is **disabled** when `value.divisionByZero === true` and we
 *     surface an inline `分母不可為零` message so the user can self-recover.
 *   - The `note` and `project` inputs are bound via `value`/`onChange`
 *     (controlled) — never via `dangerouslySetInnerHTML`. Keeps user text
 *     out of the HTML parser.
 *   - Delete in edit mode uses a BottomSheet confirm; we never call
 *     `onDelete` synchronously from the trash button.
 *
 * Currency:
 *   - Each transaction is stored with its OWN currency code. We never
 *     auto-convert. Form starts with `defaultCurrency` (typically the
 *     user's primary) and exposes a visible chip+select for switching.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react';
import type {
  BookRow,
  CategoryKind,
  CategoryRow,
  TransactionRow,
  TxKind,
} from '@/lib/sheets/types';
import {
  calcInitial,
  calcReduce,
  type CalcInput,
  type CalcState,
} from '@/lib/calculator/expression';
import CategoryPicker from './CategoryPicker';
import TagInput from './TagInput';
import BottomSheet from '@/components/common/BottomSheet';
import Icon, { type IconName } from '@/components/common/Icon';
import { displayTxIcon } from '@/lib/displayTxIcon';
import { formatAmount } from '@/lib/format/currency';
import { readDisplayTags, sanitizeTag, stringifyTags } from '@/lib/tags';
import type { RecentNotesIndex } from '@/lib/recentNotes';
import { dayPart, joinDateTime, timePart } from '@/lib/format/datetime';
import { haptic } from '@/lib/haptics';

export type AddTxMode = 'create' | 'edit';

/** Subset of `TransactionRow` the parent must produce when delegating
 *  the actual sheet write. The screen does not care about `id` or `rev`
 *  — those are owned by the sync queue / repository layer. */
export type NewTransactionInput = Omit<
  TransactionRow,
  'id' | 'rev' | 'created_at' | 'updated_at' | 'deleted_at'
>;

export interface AddTxScreenProps {
  mode: AddTxMode;
  /** Books available for the book picker. */
  books: BookRow[];
  /** Currently active book id; `book_id` defaults here. */
  activeBookId: string;
  /** Categories — both expense and income main + subs. */
  categories: CategoryRow[];
  /** Default currency when not editing (typically the user's primary). */
  defaultCurrency: string;
  /** Today's local date in `YYYY-MM-DD` form. Defaults to "now()". */
  today?: string;
  /** Prefill data when `mode === 'edit'`. */
  initial?: Partial<TransactionRow>;
  /** Tag suggestions — typically a deduped list of every tag the user has
   *  used across past transactions. Empty = no suggestion row. */
  tagSuggestions?: string[];
  /** Recent-notes index — produced by `buildRecentNotesIndex` over the
   *  user's transactions. AddTx selects from `bySubcategory`, `global`,
   *  or `allUnique` according to whether a subcategory is selected and
   *  whether the user has typed a memo query. */
  recentNotesIndex?: RecentNotesIndex;
  onClose: () => void;
  onSubmit: (tx: NewTransactionInput) => void;
  /** Edit-mode only: invoked AFTER the user confirms the delete sheet. */
  onDelete?: () => void;
  /**
   * Called when the user taps 「繼續新增」. If unset, AddTxScreen still
   * fires `onSubmit` and resets internal form state to defaults so the
   * next entry is fast.
   */
  onContinue?: (tx: NewTransactionInput) => void;
  /** Optional camera/receipt scan hook for the top-right 📷 affordance. */
  onScanReceipt?: () => void;
}

/** Common quick-pick currencies; matches the design's CURRENCIES list. */
const CURRENCY_OPTIONS = ['TWD', 'JPY', 'USD', 'EUR', 'CNY', 'HKD', 'KRW', 'GBP'] as const;

/** Display symbol prefix for the live hero amount. Mirrors the
 *  `SYMBOL_OVERRIDES` map in `lib/format/currency.ts`. The chip already shows
 *  the 3-letter code; this gives the user immediate visual confirmation
 *  ("NT$ 100", "¥ 100") so they know which currency they're entering. */
function currencySymbol(currency: string): string {
  switch (currency) {
    case 'TWD': return 'NT$';
    case 'USD': return '$';
    case 'JPY': return '¥';
    case 'EUR': return '€';
    case 'CNY': return '¥';
    case 'HKD': return 'HK$';
    case 'KRW': return '₩';
    case 'GBP': return '£';
    default: return currency;
  }
}

function todayYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function nowHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Add thousand separators to every numeric token in a calculator display
 * string while preserving operators (`+ − × ÷`), the decimal point, and
 * negative signs. The calculator state stores raw digit strings; this is
 * a render-time format only — never read back into state.
 */
function withThousands(s: string): string {
  return s.replace(/-?\d+(?:\.\d*)?/g, (numStr) => {
    const dot = numStr.indexOf('.');
    const intPart = dot === -1 ? numStr : numStr.slice(0, dot);
    const fracPart = dot === -1 ? '' : numStr.slice(dot);
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + fracPart;
  });
}

function seedCalcState(amountMinor: number): CalcState {
  if (!amountMinor || amountMinor <= 0) return calcInitial();
  // Build a state from digits so the user can edit / append further.
  const major = amountMinor / 100;
  const display = String(Number.isInteger(major) ? major : major.toFixed(2));
  let s = calcInitial();
  for (const ch of display) {
    if (ch === '.') {
      s = calcReduce(s, { type: 'dot' });
    } else if (ch >= '0' && ch <= '9') {
      s = calcReduce(s, {
        type: 'digit',
        d: Number(ch) as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9,
      });
    }
  }
  return s;
}

// Keypad layout — matches design's 4×4 grid.
// Row 4 is `00 / 0 / . / +` per design, with ⌫ moved to the hero row.
const KEY_GRID: ReadonlyArray<
  ReadonlyArray<{ label: string; input: CalcInput; op?: boolean }>
> = [
  [
    { label: '7', input: { type: 'digit', d: 7 } },
    { label: '8', input: { type: 'digit', d: 8 } },
    { label: '9', input: { type: 'digit', d: 9 } },
    { label: '÷', input: { type: 'op', op: '÷' }, op: true },
  ],
  [
    { label: '4', input: { type: 'digit', d: 4 } },
    { label: '5', input: { type: 'digit', d: 5 } },
    { label: '6', input: { type: 'digit', d: 6 } },
    { label: '×', input: { type: 'op', op: '×' }, op: true },
  ],
  [
    { label: '1', input: { type: 'digit', d: 1 } },
    { label: '2', input: { type: 'digit', d: 2 } },
    { label: '3', input: { type: 'digit', d: 3 } },
    { label: '−', input: { type: 'op', op: '-' }, op: true },
  ],
  [
    { label: '0', input: { type: 'digit', d: 0 } },
    { label: '00', input: { type: 'digit', d: 0 } },
    { label: '.', input: { type: 'dot' } },
    { label: '+', input: { type: 'op', op: '+' }, op: true },
  ],
];

export default function AddTxScreen({
  mode,
  books,
  activeBookId,
  categories,
  defaultCurrency,
  today,
  initial,
  tagSuggestions,
  recentNotesIndex,
  onClose,
  onSubmit,
  onDelete,
  onContinue,
  onScanReceipt,
}: AddTxScreenProps): JSX.Element {
  const initialKind: TxKind =
    initial?.kind === 'income' ? 'income' : 'expense';
  const [kind, setKind] = useState<TxKind>(initialKind);
  const pickerKind: CategoryKind = kind === 'income' ? 'income' : 'expense';

  // Calculator state — fully local; we only emit to onSubmit.
  const [calc, setCalc] = useState<CalcState>(() =>
    seedCalcState(initial?.amount_minor ?? 0),
  );

  const [categoryId, setCategoryId] = useState<string | null>(
    initial?.category_id ?? null,
  );
  const [subId, setSubId] = useState<string | null>(
    initial?.subcategory_id ?? null,
  );
  const [note, setNote] = useState<string>(initial?.note ?? '');
  // Lifted from TagInput so we can filter the 常用標籤 chip row by what
  // the user is typing — autocomplete-style narrowing, instant tap.
  const [tagBuffer, setTagBuffer] = useState<string>('');
  // v3 tag chips. Seeded from `initial.tags` if present; falls back to the
  // legacy `project` field on row-level via `readDisplayTags` so a tx mid-
  // migration still surfaces its single project value as one chip.
  const [tags, setTags] = useState<string[]>(() =>
    initial ? readDisplayTags(initial) : [],
  );
  // v5: `date` on TransactionRow may carry a `THH:MM` suffix. Split for
  // editing (the date input + time input each take a single component)
  // and re-join via `joinDateTime` on submit.
  const [date, setDate] = useState<string>(
    () => dayPart(initial?.date ?? '') || today || todayYmd(),
  );
  const [time, setTime] = useState<string>(
    () => timePart(initial?.date ?? '') || nowHm(),
  );
  const [bookId, setBookId] = useState<string>(
    initial?.book_id ?? activeBookId,
  );
  const [currency, setCurrency] = useState<string>(
    initial?.currency ?? defaultCurrency,
  );

  // When kind flips, reset the picker to the first matching main category.
  // We keep `subId` only if it still belongs to the new selection.
  useEffect(() => {
    const firstMain = categories.find(
      (c) => !c.deleted_at && !c.parent_id && c.kind === pickerKind,
    );
    if (!firstMain) {
      setCategoryId(null);
      setSubId(null);
      return;
    }
    const current = categories.find((c) => c.id === categoryId);
    if (!current || current.kind !== pickerKind) {
      setCategoryId(firstMain.id);
      setSubId(null);
    }
  }, [categories, pickerKind, categoryId]);

  // Confirm sheet state for delete.
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Book picker sheet
  const [openBooks, setOpenBooks] = useState(false);

  const canSubmit = useMemo(() => {
    if (calc.divisionByZero) return false;
    if (calc.amountMinor <= 0) return false;
    if (!categoryId) return false;
    return true;
  }, [calc.divisionByZero, calc.amountMinor, categoryId]);

  const dispatchCalc = useCallback(
    (input: CalcInput): void => {
      // Mobile-web haptic — short pulse on each key press feels closer
      // to a native calculator keypad. Desktop / iOS Safari no-op via
      // `lib/haptics.ts`, so this is safe to call unconditionally.
      haptic('tap');
      setCalc((prev) => calcReduce(prev, input));
    },
    [],
  );

  const buildPayload = useCallback((): NewTransactionInput | null => {
    if (!categoryId) return null;
    return {
      book_id: bookId,
      // v5: store as `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM` in a single field.
      date: joinDateTime(date, time),
      amount_minor: calc.amountMinor,
      currency,
      kind,
      category_id: categoryId,
      subcategory_id: subId ?? '',
      note,
      // v3: `project` is deprecated. New writes always pass '' so the
      // schema migration can converge. The migration also clears the cell
      // on legacy rows once `tags` is populated.
      project: '',
      tags: stringifyTags(tags),
      recurring_id: initial?.recurring_id ?? '',
    };
  }, [
    bookId,
    date,
    time,
    calc.amountMinor,
    currency,
    kind,
    categoryId,
    subId,
    note,
    tags,
    initial?.recurring_id,
  ]);

  const resetForNextEntry = useCallback((): void => {
    setCalc(calcInitial());
    setNote('');
    // keep date / book / kind / category / project / currency for fast next entry
  }, []);

  // Pending-op detection: `calc.expression` is non-empty when an operator
  // is awaiting its right operand (cleared on `=`). When pending, the
  // primary action button swaps from 「完成」 to 「= 計算」 (matches design).
  const hasPendingOp = calc.expression.length > 0;

  const handleSubmit = useCallback(() => {
    if (hasPendingOp) {
      dispatchCalc({ type: 'equals' });
      return;
    }
    if (!canSubmit) return;
    const payload = buildPayload();
    if (!payload) return;
    haptic('success');
    onSubmit(payload);
    onClose();
  }, [hasPendingOp, dispatchCalc, canSubmit, buildPayload, onSubmit, onClose]);

  const handleContinue = useCallback(() => {
    if (hasPendingOp) {
      dispatchCalc({ type: 'equals' });
      return;
    }
    if (!canSubmit) return;
    const payload = buildPayload();
    if (!payload) return;
    haptic('success');
    if (onContinue) {
      onContinue(payload);
    } else {
      onSubmit(payload);
    }
    resetForNextEntry();
  }, [
    hasPendingOp,
    dispatchCalc,
    canSubmit,
    buildPayload,
    onContinue,
    onSubmit,
    resetForNextEntry,
  ]);

  const selectedCat = categoryId
    ? categories.find((c) => c.id === categoryId)
    : null;
  const accent = selectedCat?.color_token ?? 'var(--accent)';

  const selectedSub = subId
    ? categories.find((c) => c.id === subId)
    : null;

  const catIcon = displayTxIcon(selectedCat, selectedSub) as IconName;

  const activeBook = books.find((b) => b.id === bookId);

  // Lock the body / document scroll while AddTxScreen is open so the
  // background list can't scroll behind the editor and so the editor
  // itself isn't clipped to a parent's mid-scroll viewport (the bug
  // where tapping a row deep inside SearchScreen made the edit modal
  // render off-screen at the SearchScreen container's natural top).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--cream-100)',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ────────────────────────────────────────────── */}
      <div
        style={{
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 14px)',
          paddingLeft: 16,
          paddingRight: 16,
          paddingBottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--cream-50)',
        }}
      >
        <button
          type="button"
          aria-label="關閉"
          onClick={onClose}
          style={{
            width: 34,
            height: 34,
            borderRadius: 17,
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <Icon name="x" size={20} stroke="var(--fg-1)" />
        </button>

        {/* Kind segmented control */}
        <div
          role="tablist"
          aria-label="類型"
          style={{
            display: 'inline-flex',
            background: 'var(--bg-raised)',
            padding: 3,
            borderRadius: 999,
            font: '500 13px var(--font-cjk)',
          }}
        >
          {(['expense', 'income'] as const).map((k) => {
            const on = kind === k;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setKind(k)}
                style={{
                  border: 0,
                  background: on ? 'var(--bg-surface)' : 'transparent',
                  padding: '5px 14px',
                  borderRadius: 999,
                  color: on ? 'var(--fg-1)' : 'var(--fg-2)',
                  boxShadow: on ? 'var(--shadow-1)' : 'none',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                {k === 'expense' ? '支出' : '收入'}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {onScanReceipt && (
            <button
              type="button"
              aria-label="掃描收據"
              title="掃描收據"
              onClick={onScanReceipt}
              style={{
                width: 34,
                height: 34,
                borderRadius: 17,
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon name="camera" size={20} stroke="var(--fg-2)" />
            </button>
          )}
          {/* 儲存 lives in the keypad action row (完成 / 繼續新增); the
              header copy was a duplicate that confused users. */}
        </div>
      </div>

      {/* ── Scrollable content (everything between header and keypad) ─ */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Hero amount block ─────────────────────────────── */}
        <div
          style={{
            padding: '10px 20px 14px',
            background: 'var(--cream-50)',
            borderBottom: '0.5px solid var(--border-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: accent,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name={catIcon} size={20} stroke="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: '500 11px var(--font-cjk)',
                  color: 'var(--fg-3)',
                  letterSpacing: '0.06em',
                }}
              >
                {selectedCat?.name ?? '選擇分類'}
                {selectedSub ? (
                  <span style={{ color: 'var(--fg-4)' }}> · {selectedSub.name}</span>
                ) : null}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  marginTop: 1,
                  overflow: 'hidden',
                }}
              >
                {/* Currency chip — visible <select> wrapped in a styled label */}
                <label
                  aria-label="切換幣別"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '3px 8px 3px 9px',
                    borderRadius: 999,
                    flexShrink: 0,
                    background: 'var(--bg-raised)',
                    border: '0.5px solid var(--border-2)',
                    font: '600 11px var(--font-num)',
                    color: 'var(--fg-2)',
                    letterSpacing: '0.04em',
                    cursor: 'pointer',
                    alignSelf: 'center',
                    position: 'relative',
                  }}
                >
                  {currency}
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ opacity: 0.6 }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  <select
                    aria-label="幣別"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      opacity: 0,
                      cursor: 'pointer',
                      width: '100%',
                      height: '100%',
                      border: 0,
                      background: 'transparent',
                    }}
                  >
                    {CURRENCY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                    {/* Keep an out-of-list current value renderable */}
                    {!CURRENCY_OPTIONS.includes(
                      currency as (typeof CURRENCY_OPTIONS)[number],
                    ) && <option value={currency}>{currency}</option>}
                  </select>
                </label>
                <span
                  className="num-hero"
                  style={{
                    font: '600 30px var(--font-num)',
                    fontVariantNumeric: 'tabular-nums',
                    color:
                      kind === 'income' ? 'var(--income)' : 'var(--fg-1)',
                    letterSpacing: '-0.02em',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      font: '500 14px var(--font-num)',
                      color: 'var(--fg-3)',
                      marginRight: 6,
                      letterSpacing: '0',
                    }}
                  >
                    {currencySymbol(currency)}
                  </span>
                  {/* Show running expression inline with the current operand
                      so the user reads "25 + 122" instead of seeing the
                      pending "25 +" tucked away as small grey text. After
                      `=` clears `expression` and only the result remains.
                      Format every numeric token with thousand separators
                      so 12000 reads as 12,000. */}
                  {calc.expression ? (
                    <span aria-label="計算式">
                      {withThousands(calc.expression)}
                      {withThousands(calc.display)}
                    </span>
                  ) : (
                    withThousands(calc.display)
                  )}
                </span>
              </div>
            </div>
            {/* Inline backspace — moved out of keypad so keypad grid stays uniform */}
            <button
              type="button"
              aria-label="退格"
              onClick={() => dispatchCalc({ type: 'backspace' })}
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: 'var(--bg-surface)',
                border: '0.5px solid var(--border-1)',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
                flexShrink: 0,
                opacity: calc.display === '0' ? 0.45 : 1,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--fg-2)"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 5H9l-6 7 6 7h12a2 2 0 002-2V7a2 2 0 00-2-2zM14 10l4 4M18 10l-4 4" />
              </svg>
            </button>
          </div>
          {calc.divisionByZero && (
            <div
              role="alert"
              style={{
                marginTop: 6,
                font: '500 12px var(--font-cjk)',
                color: 'var(--danger)',
              }}
            >
              分母不可為零
            </div>
          )}
        </div>

        {/* ── Main category tabs + sub-grid ─────────────────── */}
        <CategoryPicker
          kind={pickerKind}
          categories={categories}
          selectedId={categoryId}
          selectedSubId={subId}
          onChange={(cid, sid) => {
            setCategoryId(cid);
            setSubId(sid ?? null);
          }}
        />
      </div>
      {/* ↑ Scrollable body ends here. Below this, 常用/meta/keypad/action
            row all sit as fixed siblings — guaranteed glued to the
            keypad regardless of how tall the picker grew. */}

      {/* ── 「常用備註」 + 「常用標籤」 chip rows — FIXED above keypad.
           Each row is a horizontal scroller so the meta row underneath
           stays a fixed height regardless of how many suggestions exist.
           Both rows narrow as the user types into the matching input
           (備註 input filters 常用備註; tag buffer filters 常用標籤) so
           chips behave like an autocomplete tap-list. */}
        {(() => {
          const noteQuery = note.trim().toLowerCase();
          // Memo suggestion state machine (see spec
          // `AddTx Memo Suggestion Source By Subcategory And Typing State`):
          //   - typing → search the global allUnique pool by substring
          //   - sub selected, not typing → that subcategory's top 6
          //   - no sub, not typing → global top 6
          // Cap at 6 chips in every branch.
          const MAX_CHIPS = 6;
          let recent: ReadonlyArray<string>;
          if (noteQuery.length > 0) {
            const pool = recentNotesIndex?.allUnique ?? [];
            recent = pool
              .filter((n) => n.toLowerCase().includes(noteQuery))
              .slice(0, MAX_CHIPS);
          } else if (subId) {
            recent = recentNotesIndex?.bySubcategory[subId] ?? [];
          } else {
            recent = recentNotesIndex?.global ?? [];
          }
          // Tag suggestions = all known tags minus what's already chosen,
          // narrowed by the tag input buffer (raw, not yet committed) so
          // typing `#旅` instantly filters to matching pinned tags.
          const tagQueryRaw = tagBuffer.trim().toLowerCase();
          const tagQuery = tagQueryRaw.startsWith('#')
            ? tagQueryRaw.slice(1)
            : tagQueryRaw;
          const tagPicks = (tagSuggestions ?? []).filter((s) => {
            const tag = sanitizeTag(s);
            if (tag == null) return false;
            if (tags.includes(tag)) return false;
            if (tagQuery.length === 0) return true;
            // Match against the tag body (post-`#`) so the user can
            // type `旅` or `#旅` and either narrows to `#旅行`-style tags.
            const body = tag.startsWith('#') ? tag.slice(1) : tag;
            return body.toLowerCase().includes(tagQuery);
          });
          if (recent.length === 0 && tagPicks.length === 0) return null;
          return (
            <>
              {recent.length > 0 && (
                <div
                  style={{
                    padding: '6px 14px 0',
                    background: 'var(--cream-100)',
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="no-scrollbar"
                    style={{
                      display: 'flex',
                      gap: 6,
                      overflowX: 'auto',
                      paddingBottom: 2,
                    }}
                  >
                    <div
                      style={{
                        font: '500 10.5px var(--font-cjk)',
                        color: 'var(--fg-4)',
                        letterSpacing: '0.06em',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        paddingRight: 2,
                      }}
                    >
                      常用備註
                    </div>
                    {recent.map((name) => {
                      const on = note === name;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setNote(name)}
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            borderRadius: 999,
                            background: on ? 'var(--accent)' : 'var(--bg-surface)',
                            color: on ? '#fff' : 'var(--fg-2)',
                            border:
                              '0.5px solid ' +
                              (on ? 'var(--accent)' : 'var(--border-1)'),
                            font: '500 11.5px var(--font-cjk)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {tagPicks.length > 0 && (
                <div
                  style={{
                    padding: '4px 14px 0',
                    background: 'var(--cream-100)',
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="no-scrollbar"
                    style={{
                      display: 'flex',
                      gap: 6,
                      overflowX: 'auto',
                      paddingBottom: 2,
                    }}
                  >
                    <div
                      style={{
                        font: '500 10.5px var(--font-cjk)',
                        color: 'var(--fg-4)',
                        letterSpacing: '0.06em',
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        paddingRight: 2,
                      }}
                    >
                      常用標籤
                    </div>
                    {tagPicks.map((s) => {
                      const tag = sanitizeTag(s);
                      if (tag == null) return null;
                      return (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            if (!tags.includes(tag)) setTags([...tags, tag]);
                          }}
                          style={{
                            flexShrink: 0,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 10px',
                            borderRadius: 999,
                            background: 'var(--bg-surface)',
                            color: 'var(--fg-2)',
                            border: '0.5px solid var(--border-1)',
                            font: '500 11.5px var(--font-cjk)',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* ── Meta chip row — FIXED above keypad. ── */}
        <div
          style={{
            padding: '6px 14px 8px',
            background: 'var(--cream-100)',
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              borderRadius: 999,
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            <Icon name="calendar" size={13} stroke="var(--fg-3)" />
            <input
              aria-label="日期"
              type="date"
              value={date}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setDate(e.target.value)
              }
              style={{
                border: 0,
                background: 'transparent',
                font: 'inherit',
                color: 'inherit',
                outline: 'none',
                padding: 0,
              }}
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              borderRadius: 999,
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            {/* HH:MM time picker — defaults to "now" on a fresh entry so
                記帳 feels accurate without forcing the user to set it. */}
            <Icon name="clock" size={13} stroke="var(--fg-3)" />
            <input
              aria-label="時間"
              type="time"
              value={time}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTime(e.target.value)
              }
              style={{
                border: 0,
                background: 'transparent',
                font: 'inherit',
                color: 'inherit',
                outline: 'none',
                padding: 0,
              }}
            />
          </label>

          <button
            type="button"
            aria-label="選擇帳本"
            onClick={() => setOpenBooks(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 10px',
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              borderRadius: 999,
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-1)',
              cursor: 'pointer',
            }}
          >
            <Icon name="book" size={13} stroke="var(--fg-3)" />
            {activeBook?.name ?? '帳本'}
          </button>

          <div style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
            {/* Suggestions are rendered as a dedicated 「常用標籤」 row above
                the meta strip so the note <input> here keeps a stable size
                regardless of how many tags the user has accumulated. */}
            <TagInput
              value={tags}
              onChange={setTags}
              suggestions={[]}
              placeholder="#標籤"
              buffer={tagBuffer}
              onBufferChange={setTagBuffer}
            />
          </div>

          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 10px',
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              borderRadius: 999,
              font: '400 12px var(--font-cjk)',
              color: note ? 'var(--fg-1)' : 'var(--fg-3)',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Icon name="note" size={13} stroke="var(--fg-3)" />
            <input
              aria-label="備註"
              placeholder="備註"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{
                border: 0,
                background: 'transparent',
                font: 'inherit',
                color: 'inherit',
                outline: 'none',
                padding: 0,
                flex: 1,
                minWidth: 0,
              }}
            />
          </span>
        </div>

        {/* ── Edit-mode delete row — FIXED above keypad. ── */}
        {mode === 'edit' && (
          <div
            style={{
              padding: '4px 16px 14px',
              background: 'var(--cream-100)',
              flexShrink: 0,
            }}
          >
            <button
              type="button"
              aria-label="刪除"
              onClick={() => setConfirmDelete(true)}
              style={{
                width: '100%',
                padding: 12,
                background: 'var(--bg-surface)',
                color: 'var(--danger)',
                border: '1px solid var(--danger)',
                borderRadius: 999,
                font: '500 14px var(--font-cjk)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Icon name="trash" size={16} stroke="var(--danger)" />
              刪除此筆
            </button>
          </div>
        )}

      {/* ── Keypad — pinned to bottom ─────────────────────────── */}
      <div
        style={{
          padding: '6px 8px 28px',
          background: 'var(--cream-100)',
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 6,
          alignContent: 'start',
          flexShrink: 0,
        }}
      >
        {KEY_GRID.flat().map((k, i) => {
          const isOp = k.op === true;
          const ariaLabel = (() => {
            if (k.label === '÷') return '除';
            if (k.label === '×') return '乘';
            if (k.label === '−') return '減';
            if (k.label === '+') return '加';
            if (k.label === '.') return '小數點';
            return k.label;
          })();
          return (
            <button
              key={k.label + i}
              type="button"
              aria-label={ariaLabel}
              onClick={() => {
                if (k.label === '00') {
                  // "00" = two zero digits
                  dispatchCalc({ type: 'digit', d: 0 });
                  dispatchCalc({ type: 'digit', d: 0 });
                  return;
                }
                dispatchCalc(k.input);
              }}
              style={{
                height: 38,
                borderRadius: 10,
                background: 'var(--bg-surface)',
                border: '0.5px solid var(--border-1)',
                color: isOp ? 'var(--terracotta-600)' : 'var(--fg-1)',
                font: `${isOp ? 600 : 500} 17px var(--font-num)`,
                cursor: 'pointer',
                boxShadow: '0 1px 0 rgba(58,45,31,0.04)',
              }}
            >
              {k.label}
            </button>
          );
        })}

        {/* Action row — 清除 / 繼續新增 / 完成 */}
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6,
            marginTop: 2,
          }}
        >
          <button
            type="button"
            onClick={() => dispatchCalc({ type: 'clear' })}
            style={{
              height: 42,
              borderRadius: 12,
              background: 'var(--bg-surface)',
              border: '0.5px solid var(--border-1)',
              font: '500 14px var(--font-cjk)',
              color: 'var(--fg-2)',
              cursor: 'pointer',
            }}
          >
            清除
          </button>
          <div
            style={{
              gridColumn: '2 / -1',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 6,
            }}
          >
            <button
              type="button"
              onClick={handleContinue}
              disabled={!hasPendingOp && !canSubmit}
              style={{
                height: 42,
                borderRadius: 12,
                background: 'var(--bg-surface)',
                border: '1px solid var(--accent)',
                font: '600 14px var(--font-cjk)',
                color:
                  hasPendingOp || canSubmit ? 'var(--accent)' : 'var(--fg-4)',
                cursor:
                  hasPendingOp || canSubmit ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                opacity: hasPendingOp || canSubmit ? 1 : 0.6,
              }}
            >
              {/* Design's 繼續新增 keeps its label even when hasOp; only its
                  CLICK behavior swaps to evaluate the expression. The right
                  primary button is the one that visually swaps to `= 計算`. */}
              <Icon
                name="plus"
                size={14}
                stroke={hasPendingOp || canSubmit ? 'var(--accent)' : 'var(--fg-4)'}
              />
              繼續新增
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!hasPendingOp && !canSubmit}
              style={{
                height: 42,
                borderRadius: 12,
                background: 'var(--accent)',
                color: '#fff',
                border: 0,
                font: '600 15px var(--font-cjk)',
                cursor:
                  hasPendingOp || canSubmit ? 'pointer' : 'not-allowed',
                boxShadow: '0 4px 12px rgba(155,94,64,0.22)',
                opacity: hasPendingOp || canSubmit ? 1 : 0.55,
              }}
            >
              {hasPendingOp ? '= 計算' : '完成'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Currency-summary breadcrumb (for screenreaders) ─── */}
      <span style={visuallyHidden}>
        {currency} {formatAmount(calc.amountMinor, currency)}
      </span>

      {/* ── Confirm-delete bottom sheet ──────────────────────── */}
      <BottomSheet
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="刪除這筆紀錄？"
      >
        <div
          style={{
            font: '400 14px var(--font-cjk)',
            color: 'var(--fg-2)',
            paddingBottom: 16,
          }}
        >
          確定要刪除這筆紀錄嗎？此操作會將該筆紀錄標記為刪除，可在
          通知中心找回。
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--bg-raised)',
              color: 'var(--fg-1)',
              border: 0,
              borderRadius: 12,
              font: '500 14px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmDelete(false);
              onDelete?.();
              onClose();
            }}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--danger)',
              color: '#fff',
              border: 0,
              borderRadius: 12,
              font: '600 14px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            確定刪除
          </button>
        </div>
      </BottomSheet>

      {/* ── Book picker ─────────────────────────────────────── */}
      <BottomSheet
        open={openBooks}
        onClose={() => setOpenBooks(false)}
        title="選擇帳本"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {books
            .filter((b) => !b.deleted_at)
            .map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  setBookId(b.id);
                  setOpenBooks(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 8px',
                  background: 'transparent',
                  border: 0,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    background: b.color_token,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    font: '500 14px var(--font-cjk)',
                    color: 'var(--fg-1)',
                  }}
                >
                  {b.name}
                </span>
              </button>
            ))}
        </div>
      </BottomSheet>
    </div>
  );
}

const visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  border: 0,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
} as const;
